// src/oura/components/visibility/QuestionHistory.tsx
//
// Question history: every question set the business has been measured on, and
// what changed between two of them. Staff and client see the same history;
// only the wording around it differs. Opened from the page's
// "Question history" button, a version tab on Named over time or the change
// banner, and kept in the URL (`?sets=3&vs=2`) like the page's other drawers.
//
// Read like a changelog, not a report: the jump between the two sets on one
// line, split into real change and the swap, then three short lists (added,
// removed, reworded) with one dot per question for its verdict, and the
// questions both sets asked folded away. The versions sit on the right.
// Restore and a preview before a re-baseline need the backend and are not
// offered here.

import { useState, type ReactNode } from "react";
import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, GitCompareIcon, ListIcon } from "lucide-react";
import { Drawer, KindChip, PillButton, Seg, SURFACE, Tag, TYPE, FOCUS_RING } from "@oura/ui";
import { MERCHANT_V2 } from "../../merchantV2";
import { VISIBILITY_CHECK_ORDER } from "../../audit";
import { VISIBILITY_CHECK_LABEL } from "../../lib/visibilityChecks";
import type { AeoQuery } from "@service/aeo";
import { cn } from "../../lib/cn";
import { useWritesPaused } from "@context/maintenance";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { crowdedDays, dayMonth, scanWhen } from "../../visibility/format";
import {
    diffQuestionSets,
    questionKey,
    setJump,
    versionName,
    versionPublished,
    type VersionPublished,
    type QuestionSetDiff,
    type QuestionSetVersion,
    type ScanQuestions,
} from "../../visibility/questionSets";

type Scans = Record<string, ScanQuestions | null | undefined>;

const first = (v: QuestionSetVersion) => v.scans[0];
const last = (v: QuestionSetVersion) => v.scans[v.scans.length - 1];
const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${Math.abs(n)}`;

/** Questions that share an area or service sit together, then alphabetically. */
const byFill = (a: AeoQuery, b: AeoQuery) => (a.fill ?? "~").localeCompare(b.fill ?? "~") || a.text.localeCompare(b.text);

/** The diff between version `a`'s last scan and version `b`'s first, when both have loaded. */
function pairDiff(a: QuestionSetVersion, b: QuestionSetVersion, scans: Scans) {
    const sa = scans[last(a).assessmentId];
    const sb = scans[first(b).assessmentId];
    if (sa === undefined || sb === undefined) return { state: "loading" as const };
    if (sa === null || sb === null) return { state: "error" as const };
    return { state: "ready" as const, diff: diffQuestionSets(sa.queries, sb.queries), sa, sb };
}

/** Green when the scan named the business on it, a ring when it did not, nothing when unknown. */
function NamedDot({ named }: { named: boolean | undefined }) {
    if (named === undefined) return <span aria-hidden className="mt-[7px] h-2 w-2 shrink-0" />;
    return (
        <span
            title={named ? "Named" : "Not named"}
            className={cn("mt-[7px] h-2 w-2 shrink-0 rounded-full", named ? "bg-emerald-500" : "ring-[1.5px] ring-inset ring-gray-300")}
        />
    );
}

function Row({ query, scan, was, muted }: { query: AeoQuery; scan: ScanQuestions; was?: AeoQuery; muted?: boolean }) {
    return (
        <div role="listitem" className="flex items-start gap-3 py-2.5">
            <NamedDot named={scan.named?.[questionKey(query.text)]} />
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", muted ? "text-gray-500" : "text-gray-900")}>{query.text}</span>
                {was && <span className="mt-0.5 block text-xs text-gray-400">Was: {was.text}</span>}
            </span>
            {query.fill && <span className="shrink-0 pt-0.5 text-xs text-gray-400">{query.fill}</span>}
        </div>
    );
}

function Section({ title, count, note, children }: { title: string; count: number; note?: string | null; children: ReactNode }) {
    return (
        <section className="rounded-2xl bg-white px-4 ring-1 ring-black/[0.04]">
            <header className="flex items-baseline gap-2 border-b border-gray-100 py-3">
                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>{title}</span>
                <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{count}</span>
                {note && <span className={cn(TYPE.tc, "ml-auto text-gray-500")}>{note}</span>}
            </header>
            <div role="list" className="divide-y divide-gray-100">{children}</div>
        </section>
    );
}

const namedIn = (qs: AeoQuery[], scan: ScanQuestions) => qs.filter((q) => scan.named?.[questionKey(q.text)] === true).length;

function Diff({ diff, before, after }: { diff: QuestionSetDiff; before: ScanQuestions; after: ScanQuestions }) {
    const [sameOpen, setSameOpen] = useState(false);
    const jump = setJump(diff, before, after);
    return (
        <div className="space-y-3">
            {jump && (
                <div className="rounded-2xl bg-white px-4 py-3.5 ring-1 ring-black/[0.04]">
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 tabular-nums">
                        <span className={cn(TYPE.tt, "text-gray-900")}>
                            {jump.before} <span className="font-normal text-gray-400">→</span> {jump.after}
                        </span>
                        {jump.real !== null && (
                            <span className={cn(TYPE.tc, "text-gray-500")}>
                                <span className={cn("font-semibold", jump.real >= 0 ? "text-emerald-700" : "text-rose-600")}>{signed(jump.real)}</span> real change
                            </span>
                        )}
                        {jump.fromQuestions !== null && (
                            <span className={cn(TYPE.tc, "text-gray-500")}>
                                <span className="font-semibold text-amber-700">{signed(jump.fromQuestions)}</span> from the swap
                            </span>
                        )}
                    </div>
                    <p className="m-0 mt-1 text-xs text-gray-500">
                        {jump.sameDay ? `Both sets were asked on ${dayMonth(after.capturedAt)}. ` : ""}
                        {jump.real !== null
                            ? `Real change counts the ${diff.kept.length} questions both sets asked.`
                            : `Only ${diff.kept.length} questions are in both sets, too few to split the jump.`}
                    </p>
                </div>
            )}

            <div className="flex items-center justify-end gap-4 px-1 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-emerald-500" />Named</span>
                <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full ring-[1.5px] ring-inset ring-gray-300" />Not named</span>
            </div>

            {diff.added.length > 0 && (
                <Section title="Added" count={diff.added.length} note={after.named ? `${namedIn(diff.added, after)} named` : null}>
                    {[...diff.added].sort(byFill).map((q) => <Row key={q.text} query={q} scan={after} />)}
                </Section>
            )}
            {diff.removed.length > 0 && (
                <Section title="Removed" count={diff.removed.length} note={before.named ? `${namedIn(diff.removed, before)} ${namedIn(diff.removed, before) === 1 ? "was" : "were"} named` : null}>
                    {[...diff.removed].sort(byFill).map((q) => <Row key={q.text} query={q} scan={before} muted />)}
                </Section>
            )}
            {diff.reworded.length > 0 && (
                <Section title="Reworded" count={diff.reworded.length}>
                    {diff.reworded.map((r) => <Row key={r.now.text} query={r.now} scan={after} was={r.was} />)}
                </Section>
            )}
            {diff.added.length + diff.removed.length + diff.reworded.length === 0 && (
                <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>No question changed.</p>
            )}

            {diff.kept.length > 0 && (sameOpen ? (
                <Section title="Asked in both" count={diff.kept.length}>
                    {[...diff.kept].sort(byFill).map((q) => <Row key={q.text} query={q} scan={after} />)}
                </Section>
            ) : (
                <button
                    type="button"
                    onClick={() => setSameOpen(true)}
                    className={cn("inline-flex items-center gap-1 px-1 text-[13px] font-medium text-blue-600 hover:underline", FOCUS_RING)}
                >
                    {diff.kept.length} asked in both sets
                    <ChevronRightIcon aria-hidden size={14} />
                </button>
            ))}
        </div>
    );
}

export function QuestionHistory({
    open,
    staff,
    versions,
    selected,
    compareWith,
    currentVersion,
    scans,
    onSelect,
    onCompare,
    onClose,
    nextCheckAt,
    onPublish,
}: {
    open: boolean;
    staff: boolean;
    /**
     * Staff: publish or hide a version for the client. Answers with the reason
     * it stopped, or null. Absent for the client, who has no switch.
     */
    onPublish?: (version: QuestionSetVersion, on: boolean, notify: boolean) => Promise<string | null>;
    /** The merchant's next AI check, for the "no changes yet" note. */
    nextCheckAt?: string | null;
    versions: QuestionSetVersion[];
    /** The version on view, by number. */
    selected: number;
    /** The earlier version it is compared with; the one before it when null. */
    compareWith: number | null;
    currentVersion: number | null;
    scans: Scans;
    onSelect: (n: number) => void;
    onCompare: (n: number) => void;
    onClose: () => void;
}) {
    const b = versions.find((v) => v.n === selected) ?? versions[versions.length - 1];
    if (!open || !b) return null;
    const earlier = versions.filter((v) => v.n < b.n);
    const a = earlier.find((v) => v.n === compareWith) ?? earlier[earlier.length - 1] ?? null;
    const pair = a ? pairDiff(a, b, scans) : null;
    // Two scans on one day are told apart by their time.
    const crowded = crowdedDays(versions.flatMap((v) => v.scans.map((sc) => sc.capturedAt)));
    const when = (iso: string) => scanWhen(iso, crowded);
    // The backend's own words, when they say more than the generic stamp.
    // Staff only: it is written for us ("instrument changed from openai_api
    // to chatgpt_app") and means nothing to a client.
    const reason = staff && b.reason && !/^the question set changed here$/i.test(b.reason.trim()) ? b.reason : null;

    if (!staff && MERCHANT_V2) {
        return (
            <MerchantHistory
                // A new set, or a new pair, starts fresh: a type tab picked on
                // one set must not filter the next one down to nothing.
                key={`${b.n}-${a?.n ?? 0}`}
                versions={versions}
                b={b}
                a={a}
                pair={pair}
                scans={scans}
                currentVersion={currentVersion}
                when={when}
                nextCheckAt={nextCheckAt ?? null}
                earlier={earlier}
                onSelect={onSelect}
                onCompare={onCompare}
                onClose={onClose}
            />
        );
    }

    let body;
    if (!a) {
        const scan = scans[first(b).assessmentId];
        body = (
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                The first set{scan ? `, ${scan.queries.length} questions` : ""}. Nothing earlier to compare it with.
            </p>
        );
    } else if (!pair || pair.state === "loading") {
        body = <p className={cn(TYPE.tc, "m-0 text-gray-500")} aria-busy>Reading the two scans…</p>;
    } else if (pair.state === "error") {
        body = <p className={cn(TYPE.tc, "m-0 text-gray-500")}>One of the two scans could not be read. Refresh the page to retry.</p>;
    } else {
        body = <Diff key={`${a.n}-${b.n}`} diff={pair.diff} before={pair.sa} after={pair.sb} />;
    }

    return (
        <Drawer
            open
            onClose={onClose}
            title="Question history"
            sub={staff
                ? "A rescan asks the current set again. To change the questions, use Rescan and choose Edit the questions."
                : "Every set of questions we have asked the AI assistants about you, and what changed between them."}
            width={920}
            itemKey={`${b.n}-${a?.n ?? 0}`}
            foot={<PillButton onClick={onClose}>Close</PillButton>}
        >
            <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_200px]">
                <div className="min-w-0">
                    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 px-1">
                        <span className={cn(TYPE.tt, "text-gray-900")}>
                            {a ? <>{versionName(a.n)} <span className="font-normal text-gray-400">→</span> </> : null}
                            {versionName(b.n)}
                        </span>
                        <span className={cn(TYPE.tc, "text-gray-500")}>{when(first(b).capturedAt)}</span>
                        {a && earlier.length > 1 && (
                            <select
                                aria-label="Compare with"
                                value={a.n}
                                onChange={(e) => onCompare(Number(e.target.value))}
                                className={cn("ml-auto h-8 rounded-full bg-white pl-3 pr-8 text-[13px] font-medium text-gray-700 ring-1 ring-inset ring-gray-200", FOCUS_RING)}
                            >
                                {earlier.map((v) => <option key={v.n} value={v.n}>Compare with {versionName(v.n)}</option>)}
                            </select>
                        )}
                    </div>
                    {reason && <p className={cn(TYPE.tc, "m-0 mb-3 px-1 text-gray-500")}>{reason}</p>}
                    {staff && onPublish && versionPublished(b) !== null && (
                        <PublishVersion key={b.n} version={b} versions={versions} onPublish={onPublish} />
                    )}
                    {body}
                </div>

                <nav aria-label="Question sets" className="order-first md:order-none">
                    <p className={cn(TYPE.lab, "m-0 mb-2 px-3")}>Versions</p>
                    <div role="list" className="space-y-1">
                        {[...versions].reverse().map((v) => {
                            const on = v.n === b.n;
                            const end = last(v);
                            return (
                                <div role="listitem" key={v.n}>
                                    <button
                                        type="button"
                                        aria-current={on ? "true" : undefined}
                                        onClick={() => onSelect(v.n)}
                                        className={cn(
                                            "w-full rounded-xl px-3 py-2.5 text-left transition-colors",
                                            on ? "bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.06]" : "hover:bg-white/70",
                                            FOCUS_RING,
                                        )}
                                    >
                                        <span className="flex items-baseline gap-2">
                                            <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>{versionName(v.n)}</span>
                                            {v.n === currentVersion && <span className="text-xs font-medium text-blue-600">Current</span>}
                                            <span className="ml-auto text-xs tabular-nums text-gray-500">{when(first(v).capturedAt)}</span>
                                        </span>
                                        <span className="mt-0.5 block text-xs tabular-nums text-gray-500">
                                            {end.named !== null && end.of !== null ? `${end.named} of ${end.of} named · ` : ""}
                                            {v.scans.length} {v.scans.length === 1 ? "scan" : "scans"}
                                        </span>
                                        {staff && onPublish && <PublishedLine state={versionPublished(v)} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </nav>
            </div>
        </Drawer>
    );
}

/* ------------------------------------------------------------------------- *
 * Publish a version to the client (staff)
 *
 * A version is shown to the client exactly when its scans are published: the
 * same `published_at` "Publish to client" sets on one check. So the switch
 * publishes, or hides, every scan of the version, and a new version starts
 * hidden because its first scan finishes held. The client's own history,
 * chart and change banner then hold only what is published, numbered in
 * their own order.
 * ------------------------------------------------------------------------- */

const PUBLISHED_WORD: Record<VersionPublished, string> = {
    yes: "Published",
    no: "Not published",
    partly: "Partly published",
};

function PublishedLine({ state }: { state: VersionPublished | null }) {
    if (!state) return null;
    return (
        <span className={cn("mt-1 flex items-center gap-1.5 text-xs font-medium", state === "yes" ? "text-emerald-700" : state === "partly" ? "text-amber-700" : "text-gray-500")}>
            <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", state === "yes" ? "bg-emerald-500" : state === "partly" ? "bg-amber-500" : "bg-gray-300")} />
            {PUBLISHED_WORD[state]}
        </span>
    );
}

function Switch({ on, disabled, label, onClick }: { on: boolean; disabled?: boolean; label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className={cn(
                "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                on ? "bg-blue-600" : "bg-gray-300",
                FOCUS_RING,
            )}
        >
            <span aria-hidden className={cn("inline-block h-5 w-5 rounded-full bg-white shadow transition-transform", on ? "translate-x-[18px]" : "translate-x-0.5")} />
        </button>
    );
}

function PublishVersion({
    version,
    versions,
    onPublish,
}: {
    version: QuestionSetVersion;
    versions: QuestionSetVersion[];
    onPublish: (version: QuestionSetVersion, on: boolean, notify: boolean) => Promise<string | null>;
}) {
    const [asking, setAsking] = useState<"publish" | "hide" | null>(null);
    const [notify, setNotify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const writesPaused = useWritesPaused();
    const state = versionPublished(version);
    if (!state) return null;

    const name = versionName(version.n);
    const on = state === "yes";
    const newest = version === versions[versions.length - 1];
    // What the client reads instead, while this one is hidden: the newest other version they can see.
    const others = versions.filter((v) => v.n !== version.n && versionPublished(v) !== "no");
    const fallback = others[others.length - 1] ?? null;
    const shownScans = version.scans.filter((sc) => !!sc.publishedAt).length;
    const lastOne = state !== "no" && others.length === 0;
    // The client numbers only the versions it sees, so a skipped one moves the
    // rest down: say the client's number when it is not ours.
    const clientN = state === "no" ? null : versions.filter((v) => v.n <= version.n && versionPublished(v) !== "no").length;

    const line = on
        ? `The client sees ${name} on Visibility, its chart and question history.`
        : state === "partly"
            ? `The client sees ${shownScans} of ${version.scans.length} scans of ${name}.`
            : fallback
                ? `The client does not see ${name}. They see ${versionName(fallback.n)} instead.`
                : `The client does not see ${name}, and has no other version to see yet.`;

    const go = async () => {
        setBusy(true);
        setError(null);
        const refused = await onPublish(version, asking === "publish", notify && newest);
        setBusy(false);
        if (refused) setError(refused);
        else setAsking(null);
    };

    return (
        <section aria-label={`${name} for the client`} className="mb-4 rounded-2xl bg-white px-4 py-3.5 ring-1 ring-black/[0.06]">
            <div className="flex items-start gap-3">
                {state === "partly" ? (
                    // Half shown (scans published one by one before this switch
                    // existed): both ways on, so staff can make it all one way.
                    <div className="flex shrink-0 flex-col gap-2">
                        <PillButton variant="primary" size="sm" writes disabled={busy} onClick={() => { setError(null); setAsking("publish"); }}>
                            Publish remaining
                        </PillButton>
                        <PillButton size="sm" writes disabled={busy || lastOne} onClick={() => { setError(null); setAsking("hide"); }}>
                            Hide published
                        </PillButton>
                    </div>
                ) : (
                    <Switch
                        on={on}
                        label={`Publish ${name} to the client`}
                        disabled={busy || writesPaused || (on && lastOne)}
                        onClick={() => {
                            setError(null);
                            setAsking(on ? "hide" : "publish");
                        }}
                    />
                )}
                <div className="min-w-0">
                    <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{on ? "Published to client" : PUBLISHED_WORD[state]}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{line}</p>
                    {clientN !== null && clientN !== version.n && (
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>The client sees this as Set {clientN}, because they only count published versions.</p>
                    )}
                    {state !== "no" && lastOne && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>The client must keep at least one version, so it cannot be hidden.</p>}
                </div>
            </div>
            {asking && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>
                        {asking === "publish" ? `Publish ${name} to the client?` : `Hide ${name} from the client?`}
                    </p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                        {asking === "publish"
                            ? `Its ${version.scans.length === 1 ? "scan shows" : `${version.scans.length} scans show`} on their Visibility page, chart and question history.`
                            : `It leaves their Visibility page, chart and question history.${fallback ? ` They see ${versionName(fallback.n)} instead.` : ""} Nothing is deleted.`}
                    </p>
                    {asking === "publish" && newest && (
                        <label className={cn(TYPE.tc, "mt-2 flex items-center gap-2 text-gray-700")}>
                            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                            Email the client that it is ready
                        </label>
                    )}
                    {error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-red-700")}>{error}</p>}
                    <div className="mt-3 flex gap-2">
                        <PillButton variant="primary" size="sm" busy={busy} writes onClick={() => void go()}>
                            {asking === "publish" ? "Publish" : "Hide"}
                        </PillButton>
                        <PillButton size="sm" disabled={busy} onClick={() => setAsking(null)}>Cancel</PillButton>
                    </div>
                </div>
            )}
            {!asking && error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-red-700")}>{error}</p>}
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * The merchant's history (redesign)
 * ------------------------------------------------------------------------- */

type Pair = ReturnType<typeof pairDiff> | null;

/** The set's questions, one group per question type, in the spec's order. */
function byType(queries: AeoQuery[]) {
    const groups = VISIBILITY_CHECK_ORDER.map((k) => ({ key: k as string, kind: k, list: queries.filter((q) => q.check === k) }));
    const other = queries.filter((q) => !q.check || !VISIBILITY_CHECK_ORDER.includes(q.check));
    return [...groups.filter((g) => g.list.length > 0), ...(other.length > 0 ? [{ key: "other", kind: null, list: other }] : [])];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A figure and its move: "45 → 46 ↑1", green up, red down, nothing when level. */
function Moved({ from, to }: { from: number; to: number }) {
    const change = to - from;
    return (
        <span className="tabular-nums">
            {from} <span className="font-normal text-gray-400">→</span> {to}
            {change !== 0 && (
                <span className={cn("ml-1 inline-flex items-center text-sm", change > 0 ? "text-emerald-600" : "text-rose-600")}>
                    {change > 0 ? <ArrowUpIcon aria-hidden size={13} /> : <ArrowDownIcon aria-hidden size={13} />}
                    {Math.abs(change)}
                </span>
            )}
        </span>
    );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className={SURFACE.inner}>
            <p className={cn(TYPE.tm, "m-0 text-gray-500")}>{label}</p>
            <p className={cn(TYPE.tt, "m-0 mt-0.5 text-gray-900")}>{children}</p>
        </div>
    );
}

/**
 * The words on the right of a question, for a client: the service a
 * Specialty question is about ("Airflow spa"), capitalised, and only when
 * the question itself says it: the backend can keep a stale fill after a
 * template falls back. Nothing for the other types: a Location fill repeats
 * the area every row already names, and an Intent fill is a template value
 * ("price_point_2", "the typical price") that reads as nonsense on its own.
 */
function fillLabel(q: AeoQuery): string | null {
    const fill = (q.fill ?? "").trim();
    if (q.check !== "specialty" || !fill || /[_{}]|\d$/.test(fill)) return null;
    if (!q.text.toLowerCase().includes(fill.toLowerCase())) return null;
    return fill.charAt(0).toUpperCase() + fill.slice(1);
}

/** One question, as the client reads it: the words, and the service when it says one. No dot. */
function PlainRow({ query, was, muted }: { query: AeoQuery; was?: AeoQuery; muted?: boolean }) {
    const label = fillLabel(query);
    return (
        <div role="listitem" className="flex items-start gap-3 py-2.5">
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", muted ? "text-gray-500" : "text-gray-900")}>{query.text}</span>
                {was && <span className="mt-0.5 block text-xs text-gray-500">Was: {was.text}</span>}
            </span>
            {label && <span className="shrink-0 pt-0.5 text-xs text-gray-500">{label}</span>}
        </div>
    );
}

/**
 * Why a set started, in the client's words, one sentence per code the
 * backend stamped (ourteam-backend#5914, TREND_BREAK_CODES). A code this
 * list does not know says nothing rather than something made up.
 */
const WHY_SET_CHANGED: Record<string, string> = {
    counting_rule: "A question now counts as naming you if any AI assistant names you, Google's AI included.",
    instrument: "We changed how we ask one of the AI assistants, so results before and after are kept apart.",
    model: "One of the AI assistants moved to a newer model, so results before and after are kept apart.",
    reauthored: "We rewrote the questions to fit your business more closely.",
    refilled: "We updated the questions with what your customers actually search for.",
    staff_set: "Your account manager chose this set of questions for you.",
    duplicate: "These questions were copied from an earlier scan and asked again.",
};

const CHANGE_TAG = {
    added: { tone: "green" as const, label: "Added" },
    removed: { tone: "red" as const, label: "Retired" },
    reworded: { tone: "amber" as const, label: "Reworded" },
};

function ChangeRow({ change, query, was }: { change: keyof typeof CHANGE_TAG; query: AeoQuery; was?: AeoQuery }) {
    const tag = CHANGE_TAG[change];
    return (
        <div role="listitem" className="flex items-start gap-3 py-2.5">
            <span className="shrink-0 pt-0.5"><Tag tone={tag.tone}>{tag.label}</Tag></span>
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block", change === "removed" ? "text-gray-500 line-through" : "text-gray-900")}>{query.text}</span>
                {was && <span className="mt-0.5 block text-xs text-gray-500">Was: {was.text}</span>}
            </span>
            {query.check && <span className="shrink-0"><KindChip kind={query.check} /></span>}
        </div>
    );
}

/**
 * Question history as a client reads it. One set: what the set is, its
 * questions by type, and when changes would show. Two or more: the set, how
 * the results compare (the fair figure is the questions both sets asked), and
 * what changed, one list with a label per question. The versions list only
 * appears once there is more than one version to pick from.
 */
/**
 * A question set, for the merchant: "Set 10". "V" is the scan's number on
 * their side (the picker, the chart and the reports say V16), so a set called
 * V10 on the same page read as a different, older scan (16 Oct 2026).
 */
const setName = (n: number) => `Set ${n}`;

function MerchantHistory({
    versions,
    b,
    a,
    pair,
    scans,
    currentVersion,
    when,
    nextCheckAt,
    earlier,
    onSelect,
    onCompare,
    onClose,
}: {
    versions: QuestionSetVersion[];
    b: QuestionSetVersion;
    a: QuestionSetVersion | null;
    pair: Pair;
    scans: Scans;
    currentVersion: number | null;
    when: (iso: string) => string;
    nextCheckAt: string | null;
    earlier: QuestionSetVersion[];
    onSelect: (n: number) => void;
    onCompare: (n: number) => void;
    onClose: () => void;
}) {
    const [sameOpen, setSameOpen] = useState(false);
    const scan = scans[first(b).assessmentId];
    const end = last(b);
    const ready = pair && pair.state === "ready" ? pair : null;
    const diff = ready?.diff ?? null;
    const jump = ready ? setJump(ready.diff, ready.sa, ready.sb) : null;

    const changes = diff ? diff.added.length + diff.removed.length + diff.reworded.length : 0;
    const summary = !a
        ? `Your first set: ${scan ? plural(scan.queries.length, "question") : "its questions"}.${end.named !== null ? ` Named in ${end.named}.` : ""}`
        : diff
            ? `${scan ? plural(scan.queries.length, "question") : "This set"}. ${[
                diff.added.length > 0 && `${diff.added.length} added`,
                diff.removed.length > 0 && `${diff.removed.length} retired`,
                diff.reworded.length > 0 && `${diff.reworded.length} reworded`,
            ].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " and $1") || "No question changed"}${changes > 0 ? ` since ${setName(a.n)}` : ""}.`
            : null;

    const next = nextCheckAt ? dayMonth(nextCheckAt) : null;
    const many = versions.length > 1;
    // "Scan 2 of 3": this set's latest scan among every scan the history holds.
    const allScans = versions.flatMap((v) => v.scans).sort((x, y) => Date.parse(x.capturedAt) - Date.parse(y.capturedAt));
    const scanTotal = allScans.length;
    const scanAt = allScans.findIndex((sc) => sc.assessmentId === end.assessmentId) + 1;
    // The question list's tab: All, or one question type.
    const [tab, setTab] = useState<string>("all");

    return (
        <Drawer
            open
            onClose={onClose}
            title="Question history"
            sub="The questions we ask AI assistants about you, and what changes between sets."
            width={many ? 1080 : 960}
            itemKey={`${b.n}-${a?.n ?? 0}`}
        >
            <div className={cn("grid gap-6", many && "md:grid-cols-[minmax(0,1fr)_200px]")}>
                <div className="min-w-0 space-y-3">
                    {/* What this set is. */}
                    <section className={SURFACE.block}>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={cn(TYPE.tt, "text-gray-900")}>{setName(b.n)}</span>
                            {b.n === currentVersion && <Tag tone="blue">Current</Tag>}
                            <span className={cn(TYPE.tc, "text-gray-500")}>
                                {when(end.capturedAt)} · Scan {scanAt} of {scanTotal}
                            </span>
                            {a && earlier.length > 1 && (
                                // The house dropdown (style guide 6a), as To do and Visibility use.
                                <Select value={String(a.n)} onValueChange={(v) => onCompare(Number(v))}>
                                    <SelectTrigger aria-label="Compare with" className="ml-auto h-9 w-[200px] flex-shrink-0 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {earlier.map((v) => (
                                            <SelectItem key={v.n} value={String(v.n)}>
                                                Compare with {setName(v.n)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                        {summary && <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>{summary}</p>}
                        {/* Why this set started, from the backend's codes; a set
                            from before codes existed shows no line. */}
                        {b.codes.some((c) => WHY_SET_CHANGED[c]) && (
                            <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-700")}>
                                <span className="font-medium text-gray-900">Why: </span>
                                {b.codes.flatMap((c) => (WHY_SET_CHANGED[c] ? [WHY_SET_CHANGED[c]] : [])).join(" ")}
                            </p>
                        )}
                        {!a && scan && (
                            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                                {byType(scan.queries).filter((g) => g.kind).map((g) => (
                                    <Stat key={g.key} label={VISIBILITY_CHECK_LABEL[g.kind!]}>{g.list.length}</Stat>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Straight under the set it is about, not at the foot of a long list. */}
                    {!a && (
                        <section className={cn(SURFACE.block, "flex items-start gap-3")}>
                            <GitCompareIcon aria-hidden size={18} className="mt-0.5 shrink-0 text-gray-400" />
                            <div className="min-w-0">
                                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>No changes yet</p>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                    When we add or retire questions, you will see what changed here, and how your results compare across sets.
                                    {next ? ` Your next check is on ${next}.` : ""}
                                </p>
                            </div>
                        </section>
                    )}
                    {a && (!pair || pair.state === "loading") && (
                        <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")} aria-busy>Reading the two sets…</p>
                    )}
                    {a && pair?.state === "error" && (
                        <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>One of the two sets could not be read. Refresh the page to try again.</p>
                    )}

                    {/* How the results compare: the fair figure is the questions both sets asked. */}
                    {a && ready && jump && (
                        <section className={SURFACE.block}>
                            <p className={cn(TYPE.lab, "m-0 mb-2")}>{setName(a.n)} compared with {setName(b.n)}</p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                <Stat label="Questions"><Moved from={ready.sa.queries.length} to={ready.sb.queries.length} /></Stat>
                                <Stat label="Named in"><Moved from={jump.before} to={jump.after} /></Stat>
                                <Stat label="On questions in both"><Moved from={jump.keptBefore} to={jump.keptAfter} /></Stat>
                            </div>
                            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>
                                {jump.keptAfter === jump.keptBefore
                                    ? `On the ${diff!.kept.length} questions asked in both sets, you are named as often as before.`
                                    : `On the ${diff!.kept.length} questions asked in both sets, you are named in ${Math.abs(jump.keptAfter - jump.keptBefore)} ${jump.keptAfter > jump.keptBefore ? "more" : "fewer"}.`}
                            </p>
                        </section>
                    )}

                    {/* What changed: one list, a label per question. */}
                    {a && diff && (
                        <section className={SURFACE.block}>
                            <p className={cn(TYPE.lab, "m-0")}>What changed</p>
                            {changes === 0 ? (
                                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>No question changed. The set was started again so the results are compared fairly.</p>
                            ) : (
                                <div role="list" className="divide-y divide-gray-100">
                                    {diff.added.map((q) => <ChangeRow key={`a-${q.text}`} change="added" query={q} />)}
                                    {diff.removed.map((q) => <ChangeRow key={`r-${q.text}`} change="removed" query={q} />)}
                                    {diff.reworded.map((r) => <ChangeRow key={`w-${r.now.text}`} change="reworded" query={r.now} was={r.was} />)}
                                </div>
                            )}
                            {diff.kept.length > 0 && !sameOpen && (
                                <button
                                    type="button"
                                    onClick={() => setSameOpen(true)}
                                    className={cn("mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-blue-600 hover:underline", FOCUS_RING)}
                                >
                                    {diff.kept.length} asked in both sets
                                    <ChevronRightIcon aria-hidden size={14} />
                                </button>
                            )}
                            {sameOpen && diff.kept.length > 0 && (
                                <div role="list" className="mt-2 divide-y divide-gray-100 border-t border-gray-100">
                                    {[...diff.kept].sort(byFill).map((q) => <PlainRow key={q.text} query={q} />)}
                                </div>
                            )}
                        </section>
                    )}

                    {/* One set: its questions, one type at a time. */}
                    {!a && scan && (() => {
                        const groups = byType(scan.queries);
                        const shown = tab === "all" ? groups : groups.filter((g) => g.key === tab);
                        return (
                            <section className={SURFACE.block}>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <p className={cn(TYPE.lab, "m-0")}>The questions in {setName(b.n)}</p>
                                    <Seg<string>
                                        ariaLabel="Question type"
                                        size="sm"
                                        value={groups.some((g) => g.key === tab) ? tab : "all"}
                                        onChange={setTab}
                                        items={[
                                            { key: "all", label: "All", count: scan.queries.length },
                                            ...groups.map((g) => ({ key: g.key, label: g.kind ? VISIBILITY_CHECK_LABEL[g.kind] : "Other", count: g.list.length })),
                                        ]}
                                    />
                                </div>
                                {shown.map((g) => (
                                    <div key={g.key} className="mt-3">
                                        {tab === "all" && (
                                            <div className="mb-1 flex items-center gap-2">
                                                {g.kind ? <KindChip kind={g.kind} /> : <span className={cn(TYPE.tc, "font-medium text-gray-500")}>Other</span>}
                                                <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{g.list.length}</span>
                                            </div>
                                        )}
                                        <div role="list" className="divide-y divide-gray-100">
                                            {[...g.list].sort(byFill).map((q) => <PlainRow key={q.text} query={q} />)}
                                        </div>
                                    </div>
                                ))}
                            </section>
                        );
                    })()}
                    {!a && scan === undefined && <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")} aria-busy>Reading the questions…</p>}

                </div>

                {many && (
                    <nav aria-label="Question sets" className="order-first md:order-none">
                        <p className={cn(TYPE.lab, "m-0 mb-2 px-3")}>Question sets</p>
                        <div role="list" className="space-y-1">
                            {[...versions].reverse().map((v) => {
                                const on = v.n === b.n;
                                const vEnd = last(v);
                                return (
                                    <div role="listitem" key={v.n}>
                                        <button
                                            type="button"
                                            aria-current={on ? "true" : undefined}
                                            onClick={() => onSelect(v.n)}
                                            className={cn(
                                                "w-full rounded-xl px-3 py-2.5 text-left transition-colors",
                                                on ? "bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-blue-500" : "hover:bg-white/70",
                                                FOCUS_RING,
                                            )}
                                        >
                                            <span className="flex items-baseline gap-2">
                                                <span className={cn(TYPE.tb, "font-semibold text-gray-900")}>{setName(v.n)}</span>
                                                <span className="ml-auto text-xs tabular-nums text-gray-500">From {when(first(v).capturedAt)}</span>
                                            </span>
                                            {v.n === currentVersion && <span className="mt-1 block"><Tag tone="blue">Current</Tag></span>}
                                            <span className="mt-1 block text-xs tabular-nums text-gray-500">
                                                {vEnd.named !== null && vEnd.of !== null ? `${vEnd.named} of ${vEnd.of} named · ` : ""}
                                                {plural(v.scans.length, "scan")}
                                            </span>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </nav>
                )}
            </div>
        </Drawer>
    );
}

/**
 * Above the tiles on the first scan of a new set: the questions changed, how
 * much of the jump that explains, and the way into the diff. Both roles; the
 * client reads it as "we changed your questions". Gone from the next scan on,
 * when the page compares like for like again.
 */
export function SetChangeBanner({
    staff,
    from,
    to,
    before,
    after,
    onOpen,
}: {
    staff: boolean;
    from: QuestionSetVersion;
    to: QuestionSetVersion;
    /** The scan before the change, and the scan on screen; undefined while loading. */
    before: ScanQuestions | null | undefined;
    after: ScanQuestions | null | undefined;
    onOpen: () => void;
}) {
    const diff = before && after ? diffQuestionSets(before.queries, after.queries) : null;
    const jump = diff && before && after ? setJump(diff, before, after) : null;
    const counts = diff
        ? [
            `${diff.added.length} new`,
            `${diff.removed.length} removed`,
            diff.reworded.length ? `${diff.reworded.length} reworded` : null,
        ].filter(Boolean).join(", ")
        : null;
    const split = !jump
        ? ""
        : jump.real !== null && jump.fromQuestions !== null
            ? ` ${jump.before} to ${jump.after} is ${signed(jump.real)} on the questions both sets asked and ${signed(jump.fromQuestions)} from the swap.`
            : ` Only ${diff?.kept.length ?? 0} questions are in both sets, too few to say how much of ${jump.before} to ${jump.after} is the swap.`;
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 rounded-2xl bg-amber-50 px-4 py-3 text-amber-900">
            <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white text-amber-600">
                <ListIcon size={15} />
            </span>
            <p className={cn(TYPE.tc, "m-0 min-w-0 flex-1 basis-80")}>
                <span className="font-semibold">{staff ? "The questions changed" : "We changed your questions"} on {dayMonth(first(to).capturedAt)}</span>{" "}
                ({staff ? versionName(from.n) : setName(from.n)} to {staff ? versionName(to.n) : setName(to.n)}){counts ? `: ${counts}.` : "."}
                {jump?.sameDay ? ` Both sets were asked that day.` : ""}
                {split} {staff ? "Changes on this page restart from the next scan." : "Your change is measured again from your next check."}
            </p>
            <PillButton size="sm" onClick={onOpen}>See what changed</PillButton>
        </div>
    );
}
